/**
 * The sounds of the instruments, worked out sample by sample (no recordings): plucked strings by
 * Karplus–Strong with a tuned loop, the pluck's position and hardness, and the body's resonances;
 * qin harmonics, drums, wood, gongs and bells by their partials; and the hall's echo. Each is made
 * once and kept, then played back (and bent) by voices.ts, so playing a note costs two audio nodes.
 * Every noise comes from a seeded generator: the same sound every time.
 */
import { mulberry32 } from '@nine/shared';

// ───────── filters ─────────

type Biquad = readonly [number, number, number, number, number];

function biquad(kind: 'peak' | 'hp' | 'lp' | 'bp', sr: number, f: number, q: number, db = 0): Biquad {
  const w = (2 * Math.PI * Math.min(f, sr * 0.45)) / sr;
  const cos = Math.cos(w);
  const alpha = Math.sin(w) / (2 * q);
  const A = 10 ** (db / 40);
  let b: [number, number, number];
  let a: [number, number, number];
  if (kind === 'peak') {
    b = [1 + alpha * A, -2 * cos, 1 - alpha * A];
    a = [1 + alpha / A, -2 * cos, 1 - alpha / A];
  } else if (kind === 'hp') {
    b = [(1 + cos) / 2, -(1 + cos), (1 + cos) / 2];
    a = [1 + alpha, -2 * cos, 1 - alpha];
  } else if (kind === 'lp') {
    b = [(1 - cos) / 2, 1 - cos, (1 - cos) / 2];
    a = [1 + alpha, -2 * cos, 1 - alpha];
  } else {
    b = [alpha, 0, -alpha];
    a = [1 + alpha, -2 * cos, 1 - alpha];
  }
  return [b[0] / a[0], b[1] / a[0], b[2] / a[0], a[1] / a[0], a[2] / a[0]];
}

function filter(x: Float32Array, [b0, b1, b2, a1, a2]: Biquad): void {
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const x0 = x[i]!;
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
    x[i] = y0;
  }
}

function normalize(x: Float32Array, peak = 0.9): Float32Array {
  let m = 0;
  for (let i = 0; i < x.length; i++) m = Math.max(m, Math.abs(x[i]!));
  if (m > 0) for (let i = 0; i < x.length; i++) x[i] = (x[i]! * peak) / m;
  return x;
}

/** Rises over the first `secs` (a raised cosine): a real pluck or stroke takes a moment to speak. */
function fadeIn(x: Float32Array, sr: number, secs: number): Float32Array {
  const n = Math.min(x.length, Math.max(1, Math.floor(sr * secs)));
  for (let i = 0; i < n; i++) x[i]! *= 0.5 - 0.5 * Math.cos((Math.PI * i) / n);
  return x;
}

/** Fades the last `secs` to silence, so a sample never ends on a click. */
function fadeTail(x: Float32Array, sr: number, secs = 0.05): Float32Array {
  const n = Math.min(x.length, Math.floor(sr * secs));
  for (let i = 0; i < n; i++) x[x.length - 1 - i]! *= i / n;
  return x;
}

// ───────── plucked strings ─────────

export interface PluckProfile {
  /** Loop brightness 0 (dull, silk) … 1 (bright, steel). */
  readonly bright: number;
  /** Hardness of the pluck (finger pad … plectrum), 0–1. */
  readonly hard: number;
  /** Where along the string it is plucked (fraction from the bridge). */
  readonly pos: number;
  /** Seconds to fall 60 dB at this frequency. */
  readonly t60: (f: number) => number;
  /** Body resonances: [Hz, Q, dB]. */
  readonly body: readonly (readonly [number, number, number])[];
  /** The nail's or plectrum's click at the start. */
  readonly click: number;
  /** Longest sample, seconds. */
  readonly secs: number;
  /** Seconds for the string to speak (finger pad slower, plectrum faster). */
  readonly attack: number;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

export const PLUCKS = {
  // Steel-nylon strings over a long paulownia box, plucked with picks: bright, ringing, a warm body.
  zheng: {
    bright: 0.66,
    hard: 0.6,
    pos: 0.17,
    t60: (f: number) => clamp(3.6 * (196 / f) ** 0.5, 0.9, 6.5),
    body: [
      [140, 1.1, 3],
      [380, 1.5, 4],
      [1000, 2, 2.5],
      [2900, 2, 1.5],
    ],
    click: 0.1,
    attack: 0.0015,
    secs: 4.2,
  },
  // Silk strings, the bare finger and nail, plucked near the end: dark, deep and long.
  qin: {
    bright: 0.38,
    hard: 0.35,
    pos: 0.1,
    t60: (f: number) => clamp(5.5 * (130 / f) ** 0.45, 1.6, 9),
    body: [
      [85, 1, 4],
      [190, 1.3, 3.5],
      [450, 2, 1.5],
      [1600, 1, -4],
    ],
    click: 0.05,
    attack: 0.003,
    secs: 5.5,
  },
  // Plucked hard near the bridge with a nail, on a short pear-shaped body: sharp and quick.
  pipa: {
    bright: 0.82,
    hard: 0.85,
    pos: 0.09,
    t60: (f: number) => clamp(1.3 * (220 / f) ** 0.35, 0.45, 2.2),
    body: [
      [240, 1.5, 4],
      [820, 1.8, 3],
      [2500, 2, 3.5],
    ],
    click: 0.2,
    attack: 0.0008,
    secs: 2,
  },
} as const satisfies Record<string, PluckProfile>;

export type PluckKind = keyof typeof PLUCKS;

/** A plucked string at `f` Hz: Karplus–Strong with a fractional, tuned loop. */
export function pluck(sr: number, f: number, p: PluckProfile, seed: number): Float32Array {
  const t60 = p.t60(f);
  const n = Math.floor(sr * Math.min(p.secs, t60 * 1.15 + 0.25));
  const out = new Float32Array(n);
  const rnd = mulberry32(seed);
  // Loop filter: a two-point weighted average (weight a), its delay at f worked out exactly.
  const a = 0.5 + 0.5 * p.bright;
  const w = (2 * Math.PI * f) / sr;
  const re = a + (1 - a) * Math.cos(w);
  const im = -(1 - a) * Math.sin(w);
  const avgDelay = -Math.atan2(im, re) / w;
  const avgGain = Math.hypot(re, im);
  const period = sr / f;
  let L = Math.floor(period - avgDelay - 0.1);
  if (L < 2) L = 2;
  const frac = period - avgDelay - L;
  // First-order allpass for the fraction of a sample.
  const c = (1 - frac) / (1 + frac);
  const g = Math.min(0.99995, 10 ** (-3 / (t60 * f)) / avgGain);
  // The pluck: noise softened by the finger (hardness), shaped by where it is plucked.
  const ex = new Float32Array(L);
  let y = 0;
  const h = 0.15 + 0.85 * p.hard;
  for (let i = 0; i < L; i++) {
    y += h * (rnd() * 2 - 1 - y);
    ex[i] = y;
  }
  const P = Math.max(1, Math.round(p.pos * L));
  const line = new Float32Array(L);
  let mean = 0;
  for (let i = 0; i < L; i++) {
    line[i] = ex[i]! - ex[(i - P + L) % L]!;
    mean += line[i]!;
  }
  mean /= L;
  for (let i = 0; i < L; i++) line[i]! -= mean;
  let idx = 0;
  let prev = 0;
  let apx = 0;
  let apy = 0;
  for (let i = 0; i < n; i++) {
    const x = line[idx]!;
    out[i] = x;
    const avg = a * x + (1 - a) * prev;
    prev = x;
    const ap = c * avg + apx - c * apy;
    apx = avg;
    apy = ap;
    line[idx] = g * ap;
    idx = idx + 1 === L ? 0 : idx + 1;
  }
  // The pick's click: a few milliseconds of bright noise.
  const clickN = Math.floor(sr * 0.004);
  let ck = 0;
  for (let i = 0; i < clickN && i < n; i++) {
    ck += 0.45 * (rnd() * 2 - 1 - ck);
    out[i]! += ck * p.click * 2 * (1 - i / clickN) ** 2;
  }
  for (const [bf, q, db] of p.body) filter(out, biquad('peak', sr, bf, q, db));
  filter(out, biquad('hp', sr, 45, 0.7));
  return fadeTail(normalize(fadeIn(out, sr, p.attack)), sr);
}

/** 泛音: a qin harmonic, the string touched lightly at a node: a pure, bell-like tone. */
export function harmonic(sr: number, f: number, seed: number): Float32Array {
  const secs = clamp(3.8 * (520 / f) ** 0.3, 2, 4.5);
  const n = Math.floor(sr * secs);
  const out = new Float32Array(n);
  const rnd = mulberry32(seed);
  partials(
    out,
    sr,
    [
      [f, 1, secs * 0.9],
      [f * 1.0018, 0.28, secs * 0.8],
      [f * 2, 0.16, secs * 0.4],
      [f * 3, 0.05, secs * 0.2],
    ],
    0.0025,
  );
  const clickN = Math.floor(sr * 0.003);
  for (let i = 0; i < clickN; i++) out[i]! += (rnd() * 2 - 1) * 0.04 * (1 - i / clickN);
  return fadeTail(normalize(out), sr);
}

// ───────── drums, wood, gongs and bells ─────────

/** Percussion letters for the jianpu parts. */
export const DRUMS: Record<string, string> = {
  D: '堂鼓 big drum',
  d: '小鼓 small drum',
  k: '木鱼 wooden fish',
  t: '板 clappers',
  r: '梆子 woodblock',
  c: '小镲 small cymbals',
  G: '大锣 big gong',
  g: '小锣 small gong',
  B: '梵钟 temple bell',
  b: '磬 singing bowl',
  h: '碰铃 hand bells',
  w: 'waves on the shore',
  n: 'crickets',
};

/** Peak of each percussion sample: the bright, cutting ones sit lower. */
const DRUM_PEAK: Record<string, number> = { D: 0.75, t: 0.25, d: 0.6, c: 0.3, r: 0.5, k: 0.75, h: 0.5, g: 0.6, n: 0.6 };

/** Sum of decaying partials: [Hz, level, seconds to fall 60 dB, phase]. */
function partials(
  out: Float32Array,
  sr: number,
  list: readonly (readonly [number, number, number])[],
  attack = 0.002,
  bend = 0,
): void {
  // A rotating phasor and running products instead of sin and exp per sample: these are made
  // while the game runs.
  const att = Math.exp(-1 / (attack * sr));
  for (const [f, amp, t60] of list) {
    const fall = Math.exp(-6.9 / (t60 * sr));
    let re = 1;
    let im = 0;
    let env = amp;
    let rise = 1;
    let c = 1;
    let s = 0;
    for (let i = 0; i < out.length; i++) {
      // The pitch drifts (gongs): turn the step every 32 samples.
      if ((i & 31) === 0) {
        const w = (2 * Math.PI * f * (1 + bend * Math.min(1, i / sr / 2))) / sr;
        c = Math.cos(w);
        s = Math.sin(w);
        // Keep the phasor on the unit circle.
        const m = Math.hypot(re, im);
        re /= m;
        im /= m;
      }
      const r = re * c - im * s;
      im = re * s + im * c;
      re = r;
      out[i]! += env * (1 - rise) * im;
      env *= fall;
      rise *= att;
    }
  }
}

function noiseInto(out: Float32Array, sr: number, seed: number, amp: number, tau: number, kind: Biquad[]): void {
  const rnd = mulberry32(seed);
  const tmp = new Float32Array(out.length);
  for (let i = 0; i < tmp.length; i++) tmp[i] = rnd() * 2 - 1;
  for (const k of kind) filter(tmp, k);
  for (let i = 0; i < tmp.length; i++) out[i]! += amp * tmp[i]! * Math.exp(-i / sr / tau);
}

/** A drum sweep: a sine falling from `f0` to `f1` with time constant `bend`. */
function thump(out: Float32Array, sr: number, f0: number, f1: number, bend: number, tau: number, amp: number): void {
  let ph = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    ph += (2 * Math.PI * (f1 + (f0 - f1) * Math.exp(-t / bend))) / sr;
    out[i]! += amp * Math.sin(ph) * Math.exp(-t / tau);
  }
}

/** The sample for a percussion letter. */
export function drum(sr: number, letter: string): Float32Array {
  const make = (secs: number) => new Float32Array(Math.floor(sr * secs));
  let o: Float32Array;
  switch (letter) {
    case 'D': // 堂鼓: a deep skin, a slap, a little ring of the shell.
      o = make(1.3);
      thump(o, sr, 150, 70, 0.035, 0.3, 1);
      thump(o, sr, 210, 96, 0.03, 0.09, 0.25);
      noiseInto(o, sr, 11, 0.35, 0.018, [biquad('lp', sr, 1800, 0.7)]);
      break;
    case 'd': // 小鼓 / 板鼓: tight and high.
      o = make(0.5);
      thump(o, sr, 330, 190, 0.015, 0.08, 1);
      noiseInto(o, sr, 12, 0.3, 0.025, [biquad('bp', sr, 1400, 0.9), biquad('lp', sr, 4000, 0.7)]);
      break;
    case 'k': // 木鱼: a hollow wooden knock.
      o = make(0.35);
      partials(o, sr, [
        [640, 1, 0.22],
        [1460, 0.35, 0.1],
        [2380, 0.12, 0.05],
      ], 0.0006);
      noiseInto(o, sr, 13, 0.25, 0.004, [biquad('hp', sr, 1500, 0.7)]);
      break;
    case 'r': // 梆子: a high, dry woodblock.
      o = make(0.25);
      partials(o, sr, [
        [1380, 1, 0.12],
        [3100, 0.3, 0.05],
      ], 0.0004);
      noiseInto(o, sr, 14, 0.3, 0.003, [biquad('hp', sr, 2500, 0.7)]);
      break;
    case 't': // 板: two slats clapped.
      o = make(0.2);
      noiseInto(o, sr, 15, 1, 0.012, [biquad('bp', sr, 2000, 1.2), biquad('lp', sr, 5000, 0.7)]);
      partials(o, sr, [[1900, 0.4, 0.06]], 0.0003);
      break;
    case 'c': {
      // 小镲: a shimmer of close, unrelated partials over bright noise.
      o = make(1.4);
      const rnd = mulberry32(16);
      const list: [number, number, number][] = [];
      for (let i = 0; i < 18; i++) list.push([3000 + rnd() * 6500, 0.08 + rnd() * 0.08, 0.5 + rnd() * 0.8]);
      partials(o, sr, list, 0.0005);
      noiseInto(o, sr, 17, 0.35, 0.15, [biquad('hp', sr, 4500, 0.7), biquad('lp', sr, 9000, 0.7)]);
      break;
    }
    case 'G': // 大锣: a tam-tam; its high partials bloom after the stroke, the pitch sinks.
      o = make(6.5);
      partials(o, sr, [
        [66, 0.9, 5.5],
        [103, 0.7, 5],
        [151, 0.55, 4.6],
        [197, 0.45, 4],
        [243, 0.4, 3.6],
        [311, 0.3, 3.2],
        [379, 0.22, 2.6],
        [457, 0.16, 2.2],
        [533, 0.12, 1.8],
        [701, 0.08, 1.4],
      ], 0.12, -0.012);
      noiseInto(o, sr, 18, 0.3, 0.06, [biquad('lp', sr, 900, 0.7)]);
      break;
    case 'g': // 小锣: the opera's small gong; its pitch rises as it rings.
      o = make(1.8);
      partials(o, sr, [
        [520, 1, 1.3],
        [1250, 0.4, 0.7],
        [2030, 0.2, 0.4],
      ], 0.001, 0.07);
      break;
    case 'B': // 梵钟: a temple bell, the hum below the strike lasting longest.
      o = make(8);
      partials(o, sr, [
        [49, 0.55, 7.5],
        [98, 1, 6],
        [117.6, 0.45, 4.5],
        [147, 0.35, 3.8],
        [196, 0.35, 3],
        [254, 0.2, 2.2],
        [318, 0.12, 1.6],
        [99.3, 0.4, 6],
      ], 0.004);
      thump(o, sr, 120, 70, 0.02, 0.08, 0.4);
      break;
    case 'b': // 磬: a bronze bowl, beating softly.
      o = make(4.5);
      partials(o, sr, [
        [660, 1, 4],
        [661.4, 0.6, 4],
        [1821, 0.32, 1.8],
        [3564, 0.12, 0.8],
        [5874, 0.05, 0.35],
      ], 0.0015);
      break;
    case 'h': // 碰铃: two little bells touched together.
      o = make(2.2);
      partials(o, sr, [
        [2350, 1, 1.6],
        [2356, 0.6, 1.6],
        [5405, 0.3, 0.6],
        [8930, 0.1, 0.25],
      ], 0.0008);
      break;
    case 'w': {
      // A wave: rumbling in, hissing as it breaks, drawing back over the sand.
      o = make(5);
      const rnd = mulberry32(19);
      let lo = 0;
      let hi = 0;
      for (let i = 0; i < o.length; i++) {
        const t = i / sr;
        const n = rnd() * 2 - 1;
        lo += 0.02 * (n - lo);
        hi += 0.35 * (n - hi);
        const swell = Math.sin(Math.PI * Math.min(1, t / 4.8)) ** 2;
        const crest = Math.exp(-(((t - 1.8) / 0.7) ** 2));
        o[i] = swell * (lo * 1.4 + (hi - lo) * 0.25 * (0.3 + crest));
      }
      break;
    }
    case 'n': {
      // Crickets: bursts of a high chirp, three to a call.
      o = make(2);
      for (let i = 0; i < o.length; i++) {
        const t = i / sr;
        const call = t % 0.9;
        const on = call < 0.33 && call % 0.11 < 0.07 ? 1 : 0;
        o[i] = on * Math.sin(2 * Math.PI * 4400 * t) * (0.5 + 0.5 * Math.sin(2 * Math.PI * 48 * t));
      }
      filter(o, biquad('lp', sr, 6000, 0.7));
      break;
    }
    default:
      throw new Error(`no drum "${letter}"`);
  }
  return fadeTail(normalize(fadeIn(o, sr, letter === 'w' ? 0.2 : 0.0006), DRUM_PEAK[letter] ?? 0.9), sr, 0.08);
}

// ───────── the hall ─────────

/** A soft hall's impulse response, left and right: early reflections, then a darkening tail. */
export function hall(sr: number, secs: number, seed: number): [Float32Array, Float32Array] {
  const n = Math.floor(sr * secs);
  const out: [Float32Array, Float32Array] = [new Float32Array(n), new Float32Array(n)];
  for (let ch = 0; ch < 2; ch++) {
    const rnd = mulberry32(seed + ch * 101);
    const d = out[ch]!;
    const pre = Math.floor(sr * 0.018);
    let lp = 0;
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / sr;
      // The tail darkens as it fades: the low-pass closes over time.
      const k = 0.9 * Math.exp(-t / 0.9) + 0.08;
      lp += k * (rnd() * 2 - 1 - lp);
      d[i] = lp * Math.exp((-6.9 * t) / secs) * (1 - Math.exp(-t / 0.03));
    }
    for (const [ms, amp] of [
      [11 + ch * 3, 0.5],
      [19 + ch * 2, 0.35],
      [27 + ch * 5, 0.28],
      [41 - ch * 4, 0.2],
    ] as const)
      d[Math.floor((sr * ms) / 1000)]! += amp * (ch ? -1 : 1);
    normalize(d, 0.5);
  }
  return out;
}

/** White noise, for the breath of the flutes. */
export function noise(sr: number, secs: number, seed: number): Float32Array {
  const rnd = mulberry32(seed);
  const o = new Float32Array(Math.floor(sr * secs));
  for (let i = 0; i < o.length; i++) o[i] = rnd() * 2 - 1;
  return o;
}
